import type { AfterViewInit, OnInit } from '@angular/core';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
} from '@angular/core';
import { IconProp, SizeProp } from '@fortawesome/fontawesome-svg-core';
import type { DeviceInfo } from '@innogy/shared/responsive-tools';
import { MediaObserverService } from '@innogy/shared/responsive-tools';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { NgClass, AsyncPipe } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { AutomationIdDirective } from '@innogy/utils-automation-id';

import {
  IconCardAlignment,
  IconCardBackground,
  IconCardContext,
  IconCardVariant,
} from './icon-card.model';

@Component({
  selector: 'wl-icon-card-button',
  templateUrl: './icon-card-button.component.html',
  styleUrls: [
    './icon-card-button.component.ed.scss',
    './icon-card-button.component.essent.scss',
  ],
  standalone: true,
  imports: [AutomationIdDirective, NgClass, FaIconComponent, AsyncPipe],
})
export class IconCardButtonComponent implements OnInit, AfterViewInit {
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() iconName?: IconProp;

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() title = '';

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() context: IconCardContext = 'top-task';

  /**
   * NOTE: Subtitle will only be visible in the large variant
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() subTitle = '';

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() variant: IconCardVariant = 'large';

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() variantMobile: IconCardVariant = 'small';

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() alignment: IconCardAlignment = 'center';

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() alignmentMobile: IconCardAlignment = 'left';

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() background: IconCardBackground = 'white';

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() hoverBackground: IconCardBackground = 'brand';

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() iconSize?: SizeProp;

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() iconSizeMobile?: SizeProp;

  /**
   * For Essent, this will make the title $primary, for ED, this does not change anything visually.
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() useLinkStyle = false;

  // Chevron should appear to indicate a next action
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() showNext = false;

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() highlighted = false;

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() automationId?: string;

  @Output()
  clicked = new EventEmitter<void>();

  private readonly elementRef = inject(ElementRef);
  private readonly mediaObserverService = inject(MediaObserverService);

  deviceInfo$ = this.mediaObserverService.deviceInfo$;

  computedVariant$?: Observable<IconCardVariant>;
  computedAlignment$?: Observable<IconCardAlignment>;
  computedIconSize$?: Observable<SizeProp | undefined>;

  ngOnInit() {
    this.computedVariant$ = this.compute(this.variantMobile, this.variant);
    this.computedAlignment$ = this.compute(
      this.alignmentMobile,
      this.alignment
    );
    this.computedIconSize$ = this.compute(this.iconSizeMobile, this.iconSize);
  }

  ngAfterViewInit(): void {
    this.resolveMultilineStyling();
  }

  private compute<T>(mobileValue: T, desktopValue: T): Observable<T> {
    return this.deviceInfo$.pipe(
      map(({ isMobile }: DeviceInfo) => (isMobile ? mobileValue : desktopValue))
    );
  }

  /**
   * This function sets the width of the title's container to the title's width. By removing
   * the extra width on line-breaks, it enables the icon to stick to the title in the
   * small-centered variant. This is needed because flexbox will cause the title's container
   * (span) to be fullWidth if it spans across multiple rows.
   */
  @HostListener('window:resize')
  private resolveMultilineStyling(): void {
    const node = this.elementRef.nativeElement.querySelector(
      '.title[needs-fixed-width]'
    );
    if (node?.parentNode) {
      node.parentNode.style.width = null;
      const titleWidth = node.offsetWidth + 1; // Add 1px to avoid rounding errors
      node.parentNode.style.width = titleWidth + 'px';
    }
  }
}
